import { useCallback, useMemo, useState } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useWorkspacesList } from "@api/workspace.api";
import {
    useApplyReportTemplateMutation,
    useReportTemplates,
    useReportTemplateTargets,
} from "@api/reportTemplate.api";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { KICK_REPORT_TEMPLATE_NAME } from "@common/constants/reports/report-settings-template";
import { getReportTemplateFilterKey } from "@common/helpers/report-settings/report-template-filter-key";
import { ConfirmDiscardChangesModal } from "@components/invoices/NativeInvoiceDrawer/ConfirmDiscardChangesModal";
import { useNativeInvoiceCloseGuard } from "@components/invoices/NativeInvoiceDrawer/useNativeInvoiceCloseGuard";
import { TemplateDrawerTrail } from "@components/settings/ChartOfAccountsTemplates/TemplateDrawerTrail";
import { ReportSettingsEditorDrawer } from "@components/settings/ReportSettings/ReportSettingsEditorDrawer";
import type { ReportSettingsEditorSubject } from "@components/settings/ReportSettings/reportSettingsEditor";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useScope } from "@state/scopeContext.lib";
import type { ReportTemplateRow } from "../reportTemplatesViewConfig";
import {
    getApplyReportTemplateChanges,
    getApplyReportTemplateSuccessText,
    hasApplyReportTemplateChanges,
} from "./applyReportTemplateChanges";
import {
    buildApplyReportTemplateRows,
    getSavedSelection,
    parseRowId,
    type ApplyReportTemplateRow,
} from "./applyReportTemplateRows";
import { ApplyReportTemplateFooter } from "./ApplyReportTemplateFooter";
import { ApplyReportTemplateTable } from "./ApplyReportTemplateTable";
import styles from "./ApplyReportTemplate.module.scss";

export interface ApplyReportTemplateDrawerTarget {
    template: ReportTemplateRow;
    entry: "apply" | "viewApplied";
}

interface Props {
    target: ApplyReportTemplateDrawerTarget | null;
    onClose: () => void;
}

export const ApplyReportTemplateDrawer = ({ target, onClose }: Props) => {
    const template = target?.template ?? null;
    const isOpen = template !== null;
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_REPORT_SETTINGS,
    );
    const { organizationId } = useScope();
    const { toast, error } = useToaster();
    const {
        data: workspaces,
        isError: isWorkspacesError,
        refetch: refetchWorkspaces,
    } = useWorkspacesList(
        { organizationId: organizationId ?? undefined, includeEntities: true },
        { enabled: isOpen },
    );
    const {
        data: targets,
        isError: isTargetsError,
        refetch: refetchTargets,
    } = useReportTemplateTargets({ isEnabled: isOpen });
    const { data: templates } = useReportTemplates();
    const applyTemplate = useApplyReportTemplateMutation();
    const [editedSelection, setEditedSelection] =
        useState<RowSelectionState | null>(null);
    const [settingsSubject, setSettingsSubject] =
        useState<ReportSettingsEditorSubject | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const appliedTemplate = useMemo(
        () =>
            template
                ? { templateId: template.templateId, name: template.name }
                : null,
        [template],
    );
    const savedSelection = useMemo(
        () =>
            targets && appliedTemplate
                ? getSavedSelection({ targets, template: appliedTemplate })
                : {},
        [targets, appliedTemplate],
    );
    const selection = editedSelection ?? savedSelection;
    const defaultTemplate = templates?.find(({ isDefault }) => isDefault);
    const defaultTemplateName =
        defaultTemplate?.name ?? KICK_REPORT_TEMPLATE_NAME;
    const defaultTemplateKey = getReportTemplateFilterKey(
        defaultTemplate?.id ?? null,
    );

    const rows = useMemo(
        () =>
            workspaces && targets && appliedTemplate
                ? buildApplyReportTemplateRows({
                      workspaces,
                      targets,
                      template: appliedTemplate,
                      defaultTemplateName,
                      defaultTemplateKey,
                      selection,
                      canManage,
                      isTemplateArchived: template?.isArchived ?? false,
                  })
                : null,
        [
            workspaces,
            targets,
            appliedTemplate,
            defaultTemplateName,
            defaultTemplateKey,
            selection,
            canManage,
            template,
        ],
    );

    const changes = getApplyReportTemplateChanges({
        savedSelection,
        selection,
    });
    const hasChanges = hasApplyReportTemplateChanges(changes);

    const handleSelectionChange: OnChangeFn<RowSelectionState> = (updater) =>
        setEditedSelection(
            typeof updater === "function" ? updater(selection) : updater,
        );

    const handleOpenSettings = useCallback((row: ApplyReportTemplateRow) => {
        const parsedRow = parseRowId(row.id);
        setSettingsSubject({
            kind: "workspace",
            workspaceId: row.workspaceId,
            entityId:
                parsedRow.kind === "entity" ? parsedRow.entityId : undefined,
        });
        setIsSettingsOpen(true);
    }, []);

    const handleClose = () => {
        setEditedSelection(null);
        onClose();
    };

    const save = async (): Promise<boolean> => {
        if (!template) {
            return false;
        }
        try {
            await applyTemplate.mutateAsync({
                templateId: template.templateId,
                ...changes,
            });
            toast(
                getApplyReportTemplateSuccessText({
                    templateName: template.name,
                    ...changes,
                }),
            );
            return true;
        } catch {
            error(
                `Failed to save changes to "${template.name}". Nothing changed.`,
            );
            return false;
        }
    };

    const handleSave = async () => {
        await save();
    };

    const closeGuard = useNativeInvoiceCloseGuard({
        isEditable: true,
        isDirty: hasChanges,
        onClose: handleClose,
        onSave: save,
    });

    const isLoading = !rows && !isWorkspacesError && !isTargetsError;

    return (
        <Drawer
            open={isOpen}
            onClose={closeGuard.requestClose}
            size="fullScreen"
            dismissible={false}
            noPadding
            headerAddonLeft={
                <TemplateDrawerTrail
                    title={
                        target?.entry === "viewApplied"
                            ? "Clients and entities"
                            : "Apply template"
                    }
                    templateName={template?.name}
                />
            }
        >
            {target ? (
                <div className={styles.drawerContent}>
                    <div className={styles.subtitleContainer}>
                        Select clients and entities to apply this template, or
                        deselect them to remove it
                    </div>
                    <ApplyReportTemplateTable
                        rows={isLoading ? null : (rows ?? [])}
                        templateKey={
                            target.entry === "viewApplied"
                                ? getReportTemplateFilterKey(
                                      target.template.templateId,
                                  )
                                : undefined
                        }
                        isError={isWorkspacesError || isTargetsError}
                        selection={selection}
                        onSelectionChange={handleSelectionChange}
                        onRetry={() =>
                            void Promise.all([
                                refetchWorkspaces(),
                                refetchTargets(),
                            ])
                        }
                        onOpenSettings={handleOpenSettings}
                        footer={
                            <ApplyReportTemplateFooter
                                hasChanges={hasChanges}
                                isSaving={applyTemplate.isPending}
                                onDiscard={() => setEditedSelection(null)}
                                onSave={handleSave}
                            />
                        }
                    />
                    {settingsSubject ? (
                        <ReportSettingsEditorDrawer
                            open={isSettingsOpen}
                            subject={settingsSubject}
                            onClose={() => {
                                setIsSettingsOpen(false);
                                void refetchTargets();
                            }}
                        />
                    ) : null}
                    <ConfirmDiscardChangesModal
                        show={closeGuard.isDiscardModalOpen}
                        isSaving={applyTemplate.isPending}
                        onSave={closeGuard.saveAndClose}
                        onDiscard={() => {
                            closeGuard.cancelClose();
                            handleClose();
                        }}
                        onCancel={closeGuard.cancelClose}
                    />
                </div>
            ) : null}
        </Drawer>
    );
};
